import React from "react";

/**
 * PageBackdrop — the brand watermark system for interior surfaces. It
 * never competes with content: every variant applies an ivory wash and a
 * directional fade so the text always sits comfortably in the foreground.
 *
 * Each editorial section has its own bespoke artwork in the same visual
 * universe (navy / ivory / restrained gold / lighthouse / path):
 *
 *   home        — the finished hero composition (no wash)
 *   ideas       — light & possibility: faint horizon, distant point of light
 *   research    — depth & understanding: layered landscape, faint chart lines
 *   projects    — path & action: quiet illuminated path, distant lighthouse
 *   about       — the mentorship composition (person → lighthouse → path)
 *   philosophy  — darker, lighthouse-forward atmosphere (hero art)
 *   lighthouse  — full lighthouse dominance (hero art)
 *
 * The component is aria-hidden and pointer-events-none.
 */
const VARIANTS = {
    home: {
        img: "hero",
        opacity: 1,
        // No wash — the background image is the finished hero composition.
        // Responsive crop priority: lighthouse + path on small screens,
        // balanced full composition from md up.
        positionClass: "object-[68%_55%] md:object-[45%_55%]",
        wash: "none",
    },
    ideas: {
        img: "ideas",
        opacity: 0.62,
        position: "36% 70%",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.60) 0%, rgba(247,244,236,0.44) 40%, rgba(247,244,236,0.58) 100%)",
    },
    research: {
        img: "research",
        opacity: 0.55,
        position: "50% 40%",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.62) 0%, rgba(247,244,236,0.50) 45%, rgba(247,244,236,0.62) 100%)",
    },
    projects: {
        img: "projects",
        opacity: 0.62,
        position: "45% 55%",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.58) 0%, rgba(247,244,236,0.44) 50%, rgba(247,244,236,0.60) 100%)",
    },
    about: {
        img: "about",
        opacity: 0.68,
        // Top-anchored at natural scale — never stretched across the full
        // page height, so the face/head/shoulders stay intact and human-sized.
        // Mobile: a header band with the complete face above the title.
        // Desktop: face below the title, left-center, clear of the sidebar.
        area: "top-[2svh] h-[46svh] md:top-[26svh] md:h-[95svh] lg:top-[8svh]",
        positionClass: "object-[62%_20%] md:object-[73%_20%] lg:object-[27%_20%]",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.40) 0%, rgba(247,244,236,0.34) 42%, rgba(247,244,236,0.62) 80%, rgba(247,244,236,1) 100%)",
    },
    philosophy: {
        img: "philosophy",
        opacity: 0.58,
        position: "50% 50%",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.52) 0%, rgba(247,244,236,0.42) 45%, rgba(247,244,236,0.55) 100%)",
    },
    memoire: {
        // Dusk lamplight — the warmth of lived experience remembered.
        // Distinct artwork: a lantern on a stone parapet, calm sea,
        // distant lighthouse glow far right.
        img: "memoire",
        opacity: 0.48,
        position: "38% 60%",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.58) 0%, rgba(247,244,236,0.46) 45%, rgba(247,244,236,0.60) 100%)",
    },
    lighthouse: {
        img: "lighthouse",
        opacity: 0.55,
        position: "50% 45%",
        wash:
            "linear-gradient(180deg, rgba(247,244,236,0.62) 0%, rgba(247,244,236,0.52) 45%, rgba(247,244,236,0.62) 100%)",
    },
};

export default function PageBackdrop({ variant = "about", testId = "page-backdrop", soft = false }) {
    const v = VARIANTS[variant] || VARIANTS.about;
    return (
        <div
            aria-hidden="true"
            data-testid={testId}
            data-variant={variant}
            className="pointer-events-none absolute inset-0 overflow-hidden"
        >
            <div className={v.area ? `absolute inset-x-0 ${v.area}` : "absolute inset-0"}>
                <picture>
                    <source media="(max-width: 640px)"  srcSet={`/brand/${v.img}-bg-900.jpg`} />
                    <source media="(max-width: 1200px)" srcSet={`/brand/${v.img}-bg-1600.jpg`} />
                    <img
                        src={`/brand/${v.img}-bg-2400.jpg`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className={`absolute inset-0 w-full h-full object-cover ${v.positionClass || ""}`}
                        style={{ opacity: v.opacity * (soft ? 0.55 : 1), ...(v.positionClass ? {} : { objectPosition: v.position }) }}
                    />
                </picture>
                <div className="absolute inset-0" style={{ background: v.wash }} />
                {soft && (
                    /* Long-form reading pages: an extra ivory veil keeps body
                       text supreme — the artwork is felt, not read. */
                    <div className="absolute inset-0" style={{ background: "rgba(247,244,236,0.35)" }} />
                )}
            </div>
        </div>
    );
}
